/***
 *
 *   TICKET STATS ROW
 *   Presentational grid on StatCard. The maths live in utilities/ticketStats —
 *   see stats.tailwind.js for the segmented band this renders as, and why it
 *   bleeds out of the page gutter to sit flush under the header.
 *
 **********/

import React, { useMemo } from 'react';
import { Stat, Skeleton } from 'components/lib';
import Style from './stats.tailwind';
import { useTranslate } from 'app/translations';
import { findActiveTickets } from 'utilities/tickets';
import {
    calcDurationStats,
    findCompletedToday,
    findNewTickets,
} from 'utilities/ticketStats';

const formatDays = (value) => (value === null ? null : value.toFixed(2));
const formatChange = (value) => (value === null ? undefined : value.toFixed(1));

export const TicketStats = ({ tickets }) => {
    const { _t } = useTranslate();
    const isLoading = tickets?.status === 'loading' || tickets?.isLoading;
    const data = tickets?.data;

    const activeTickets = useMemo(() => findActiveTickets(data), [data]);
    const newTickets = useMemo(() => findNewTickets(data), [data]);
    const completedTodayTickets = useMemo(() => findCompletedToday(data), [data]);
    const durations = useMemo(() => calcDurationStats(data), [data]);

    if (isLoading) {
        return (
            <div className={Style.skeletonGrid}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className={Style.skeletonCell}>
                        <Skeleton className={Style.skeletonBar} />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <section className={Style.container}>
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.number')}
                icon="clipboard"
                iconColor="pink"
                value={activeTickets?.length ?? 0}
            />
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.new')}
                icon="trending-up"
                iconColor="blue"
                value={newTickets?.length ?? 0}
            />
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.completedToday')}
                icon="check"
                iconColor="green"
                value={completedTodayTickets?.length ?? 0}
            />
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.averageCompletion')}
                icon="clock"
                iconColor="purple"
                // an em dash, not 0 — "nothing completed in the window" is not
                // the same claim as "tickets complete instantly"
                value={formatDays(durations.completion) ?? '—'}
                valueLabel={durations.completion === null ? undefined : _t('days')}
                change={formatChange(durations.completionChange)}
                lowerIsBetter
            />
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.averagePending')}
                icon="clock"
                iconColor="orange"
                value={formatDays(durations.pending) ?? '—'}
                valueLabel={durations.pending === null ? undefined : _t('days')}
                change={formatChange(durations.pendingChange)}
                lowerIsBetter
            />
        </section>
    );
};
